import * as React from 'react';

import Table from 'rc-table';

import { deluxe, platinum, RowData } from '@app/pages/StaticPages/TravelInsurance/texts';

import { PhoneLink } from '@app/components/Links/PhoneLink';
import { MailLink } from '@app/components/Links/MailLink';

import '@app/scss/pages/documents-pages.scss';

interface TagProps {
	color: string;
	children: string;
}

const Tag: React.FC<TagProps> = (props: TagProps) => (
	<div
		style={{
			color: 'white',
			background: props.color,
			padding: '.25em 1em',
			borderRadius: 4,
		}}
	>
		{props.children}
	</div>
);

export const TravelInsurance = () => {
	return (
		<div
			className="documentation travel-insurance font-family_primary font-weight_regular text-size_extra-small text__primary"
			style={{
				display: 'flex',
				flexDirection: 'column',
				gap: '4rem',
			}}
		>
			<div
				className="documentation-section text-size_small col-12"
				style={{
					margin: '0 auto',
				}}
			>
				<p>
					The best reason to purchase travel insurance is the same reason you have for any other insurance:
					you cannot plan for accidents, delays or illness, nor can you schedule your vacation around them.
					Accidents, emergencies and unforeseen circumstances can happen anywhere at any time, to anyone.
					Travel insurance can give you peace of mind by protecting your vacation investment,
					health and belongings.
					Nearly 70% of all travelers choose to protect their vacation investment with travel insurance.
				</p>
				<p>
					Why take a chance?
					Remember, no one can plan for accidents.
					Your health, belongings, vacation and peace of mind are easy to insure with our comprehensive
					Travel Insured International Travel Protection Plan.
				</p>
			</div>

			<div className="documentation-section">
				<h1
					id="plan-deluxe"
					className="documentation__title text-size_soft-large content-valign_center text-uppercase"
					style={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
					}}
				>
					<div>Worldwide trip protector</div>
					<Tag color="#0AAB43">DELUXE</Tag>
				</h1>

				<div
					className="col-12 text-size_small font-weight_semi-bold"
				>
					SCHEDULE OF INSURANCE COVERAGE AND OTHER NON-INSURANCE SERVICES
				</div>
				<Table
					columns={deluxe.columns}
					data={deluxe.data}
					className="col-12 travel-insurance__table-new schema__deluxe text-size_small text__primary"
					rowClassName={(record: RowData) => {
						return record.section ? 'table__section-header text-size_medium font-weight_semi-bold text-uppercase' : '';
					}}
				/>
				<div className="col-12 text-size_xx-small">
					<div>
						* Up to the lesser of the Trip Cost paid or the limit of coverage on your confirmation of coverage
					</div>
					<div>
						** Not applicable when $0 Trip Cost displayed on Your confirmation of coverage
					</div>
					<div>
						1 Must be purchased within 14 days of the date your initial trip payment or deposit is received. Additional terms apply.
					</div>
					<div>
						Optional bundles come at an additional cost.
					</div>
				</div>
			</div>

			<div className="documentation-section">
				<h1
					id="plan-platinum"
					className="documentation__title text-size_soft-large content-valign_center text-uppercase"
					style={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
					}}
				>
					<div>Worldwide trip protector</div>
					<Tag color="#EB6236">PLATINUM</Tag>
				</h1>

				<div
					className="col-12 text-size_small font-weight_semi-bold"
				>
					SCHEDULE OF INSURANCE COVERAGE AND OTHER NON-INSURANCE SERVICES
				</div>
				<Table
					columns={platinum.columns}
					data={platinum.data}
					className="col-12 travel-insurance__table-new schema__platinum text-size_small"
					rowClassName={(record: RowData) => {
						return record.section ? 'table__section-header text-size_medium font-weight_semi-bold text-uppercase' : '';
					}}
				/>
				<div className="col-12 text-size_xx-small">
					<div>
						* Up to the lesser of the Trip Cost paid or the limit of coverage on your confirmation of coverage
					</div>
					<div>
						** Not applicable when $0 Trip Cost displayed on Your confirmation of coverage
					</div>
					<div>
						*** $500 Return air ticket cost only if $0 Trip Cost displayed for Trip Cancellation on Your confirmation of coverage
					</div>
					<div>
						1 Must be purchased within 21 days of the date your initial trip payment or deposit is received. Additional terms apply.
					</div>
					<div>
						Optional bundles come at an additional cost.
					</div>
				</div>
			</div>

			<div className="documentation-section">
				<h1
					id="disclaimer"
					className="documentation__title text-size_soft-large content-valign_center text-uppercase"
					style={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
					}}
				>
					Disclaimer
				</h1>
				<div
					style={{
						margin: '0 auto',
					}}
					className="text-size_small col-12"
				>
					<p>
						This website contains highlights of the plans developed by Travel Insured International,
						which include travel insurance coverages underwritten by United States Fire Insurance Company,
						Principal Office located in Morristown, New Jersey, under form series T7000 et al,
						T210 et al and TP-401 et al, and non-insurance Travel Assistance Services provided by C&F
						Services and Blue Ribbon Bags (on select plans only).
					</p>
					<p>
						The terms of insurance coverages in the plans may vary by jurisdiction and not all
						insurance coverages are available in all jurisdictions.
						Insurance coverages in these plans are subject to terms,
						limitations and exclusions including an exclusion for pre-existing medical conditions.
						In most states, your travel retailer is not a licensed insurance producer/agent,
						and is not qualified or authorized to answer technical questions about the terms, benefits,
						exclusions and conditions of the insurance offered or to evaluate the adequacy of your existing
						insurance coverage.
					</p>
					<p>
						Your travel retailer may be compensated for the purchase of a plan and may provide general information
						about the plans offered, including a description of the coverage and price.
						The purchase of travel insurance is not required in order to purchase any other product or service
						from your travel retailer.
					</p>
					<p>
						CA DOI toll free number is <PhoneLink phone="800-927-4357">800-927-4357</PhoneLink>.
						The cost of your plan is for the entire plan, which consists of both insurance and non-insurance components.
						Individuals looking to obtain additional information regarding the features and pricing of each
						travel plan component, please contact Travel Insured International. P.O. Box 6503, Glastonbury,
						CT 06033; <PhoneLink phone="855-752-8303">855-752-8303</PhoneLink>;
						<MailLink mail="customercare@travelinsured.com"> customercare@travelinsured.com</MailLink>;
						California license #0I13223.
					</p>
					<p>
						While Travel Insured International markets the travel insurance in these plans on behalf of USF,
						non-insurance components of the plans were added to the plans by Travel Insured and Travel Insured
						does not receive compensation from USF for providing the non-insurance components of the plans.
					</p>
				</div>
			</div>
		</div>
	);
};
